/**
 * LegalTemplates PDF Editor — form fields: native detection, heuristics, editing
 *
 * Split from the former public/js/pdf-editor.js IIFE (Phase 1, Sep 2026).
 * Page config is injected by the Blade view as window.pdfEditorConfig; pdf.js
 * and pdf-lib are bundled from npm through context.js (no CDN).
 */
import { pushHistory } from './annotations.js';
import { escapeSvg, isLikelyUnderlineRun, redrawAllPages, redrawPage } from './annotations.js';
import {
    formFillBarEl,
    formFillDetectButton,
    formFillFloatingNavEl,
    formFillFloatingNextButton,
    formFillFloatingPrevButton,
    formFillNextButton,
    formFillPrevButton,
    formFillSummaryEl,
    pagesEl,
    state,
} from './context.js';
import {
    clamp,
    fieldHasVisibleTextContent,
    getPageCanvasElement,
    getRectIntersectionArea,
    isPixelLikelyLine,
} from './core.js';
import { scheduleDraftSave } from './drafts.js';
import { isLikelyCheckedGlyph } from './text.js';
import { setStatus, showAlert } from './ui.js';
import { ensureAllPagesPrepared } from './viewport.js';

export function getFormFillFieldStats() {
    const stats = {
        total: 0,
        text: 0,
        checkbox: 0,
        editable: 0,
        readOnly: 0,
        heuristic: 0,
        native: 0,
    };

    for (const fields of state.pageFormFields.values()) {
        if (!Array.isArray(fields)) {
            continue;
        }

        fields.forEach((field) => {
            stats.total += 1;
            if (field.kind === 'checkbox') {
                stats.checkbox += 1;
            } else {
                stats.text += 1;
            }

            if (field.heuristic) {
                stats.heuristic += 1;
            } else {
                stats.native += 1;
            }

            if (field.readOnly) {
                stats.readOnly += 1;
            } else {
                stats.editable += 1;
            }
        });
    }

    return stats;
}

export function countNativeFormFields() {
    let count = 0;
    for (const fields of state.pageFormFields.values()) {
        if (!Array.isArray(fields)) {
            continue;
        }
        count += fields.filter((field) => !field.heuristic).length;
    }
    return count;
}

export function clearHeuristicFormFields({ clearOverrides = true } = {}) {
    for (const [pageNumber, fields] of state.pageFormFields.entries()) {
        if (!Array.isArray(fields) || !fields.length) {
            continue;
        }

        const keep = [];
        fields.forEach((field) => {
            if (field && field.heuristic) {
                if (clearOverrides) {
                    const key = normalizeFormFieldName(field.name, field.id);
                    state.formFieldOverrides.delete(key);
                }
                return;
            }
            keep.push(field);
        });

        state.pageFormFields.set(pageNumber, keep);
    }
}

export async function detectHeuristicFormFieldsOnDemand() {
    if (state.activeSuiteTool !== 'edit' || state.activeTool !== 'form-fill') {
        setStatus('Open Form Fill mode to run field detection');
        return;
    }

    // Detection scans every page's pixels and text, so all pages must be
    // prepared first (the progressive loader may still be working).
    if (formFillDetectButton) {
        formFillDetectButton.disabled = true;
        formFillDetectButton.textContent = 'Scanning...';
    }
    await ensureAllPagesPrepared({ announce: true });
    if (formFillDetectButton) {
        formFillDetectButton.disabled = false;
        formFillDetectButton.textContent = 'Detect Fields';
    }

    const nativeCount = countNativeFormFields();
    if (nativeCount > 0) {
        showAlert('This PDF already has native fillable fields. Auto-detect is only used when no native fields exist.');
        setStatus('Native fillable fields already detected');
        updateFormFillSummary();
        return;
    }

    clearHeuristicFormFields({ clearOverrides: true });
    const generatedCount = maybeGenerateHeuristicFormFieldFallback();
    if (generatedCount > 0) {
        redrawAllPages();
        updateFormFillSummary();
        setStatus(`Detected ${generatedCount} field${generatedCount === 1 ? '' : 's'} from underlines/lines/checkbox symbols`);
        return;
    }

    redrawAllPages();
    updateFormFillSummary();
    showAlert('Could not detect fields from this PDF. You can still use Text mode to place entries manually.');
    setStatus('No fallback fields detected');
}

export function updateFormFillSummary() {
    if (!formFillSummaryEl) {
        return;
    }

    const isVisible = state.activeSuiteTool === 'edit' && state.activeTool === 'form-fill';
    syncFormFillNavigationButtons(isVisible);
    if (!isVisible) {
        if (formFillBarEl) {
            formFillBarEl.classList.add('hidden');
        }
        formFillSummaryEl.textContent = '';
        return;
    }

    if (formFillBarEl) {
        formFillBarEl.classList.remove('hidden');
    }

    const stats = getFormFillFieldStats();
    if (stats.total === 0) {
        formFillSummaryEl.textContent = 'Detected 0 fillable fields. Click Detect Fields to scan underlines, drawn lines, and checkbox symbols.';
        if (formFillDetectButton) {
            formFillDetectButton.disabled = false;
            formFillDetectButton.textContent = 'Detect Fields';
        }
        return;
    }

    let summary = `Detected ${stats.total} fillable fields: ${stats.text} text, ${stats.checkbox} checkbox${stats.checkbox === 1 ? '' : 'es'}`;
    if (stats.readOnly > 0) {
        summary += ` (${stats.editable} editable)`;
    }
    if (stats.heuristic > 0) {
        if (stats.native === 0) {
            summary += ' (auto-detected from underlines/lines/checkbox symbols)';
        } else {
            summary += ` (${stats.heuristic} auto-detected)`;
        }
    }

    formFillSummaryEl.textContent = summary;
    if (formFillDetectButton) {
        formFillDetectButton.disabled = stats.native > 0;
        formFillDetectButton.textContent = (stats.heuristic > 0 && stats.native === 0)
            ? 'Re-detect Fields'
            : 'Detect Fields';
    }
}

export function syncFormFillNavigationButtons(isVisible = (state.activeSuiteTool === 'edit' && state.activeTool === 'form-fill')) {
    const navButtons = [
        formFillPrevButton,
        formFillNextButton,
        formFillFloatingPrevButton,
        formFillFloatingNextButton,
    ].filter(Boolean);
    if (!navButtons.length) {
        return;
    }

    const hasEditableTextFields = getOrderedEditableTextFormFields().length > 0;
    const disabled = !isVisible || !hasEditableTextFields;
    navButtons.forEach((button) => {
        button.disabled = disabled;
    });

    if (formFillFloatingNavEl) {
        const showFloating = isVisible && window.matchMedia('(max-width: 768px)').matches;
        formFillFloatingNavEl.classList.toggle('hidden', !showFloating);
        if (showFloating) {
            syncFloatingFormFillNavKeyboardOffset();
        }
    }
}

export function syncFloatingFormFillNavKeyboardOffset() {
    const rootEl = document.documentElement;
    if (!rootEl) {
        return;
    }

    let keyboardOffset = 0;
    if (window.matchMedia('(max-width: 768px)').matches && window.visualViewport) {
        const viewport = window.visualViewport;
        const layoutHeight = window.innerHeight || document.documentElement.clientHeight || 0;
        const occludedHeight = layoutHeight - viewport.height - viewport.offsetTop;
        keyboardOffset = Math.max(0, Math.round(occludedHeight));
    }

    rootEl.style.setProperty('--pdf-floating-nav-keyboard-offset', `${keyboardOffset}px`);
}

export function hasPendingFormFieldChanges() {
    return state.formFieldOverrides.size > 0;
}

export function collectKnownFormFieldNames() {
    const names = new Set();
    state.pageFormFields.forEach((fields) => {
        (fields || []).forEach((field) => {
            names.add(normalizeFormFieldName(field.name, field.id));
        });
    });
    return names;
}

export function normalizeFormFieldName(value, fallback = '') {
    const raw = String(value || '').trim();
    if (raw) {
        return raw;
    }

    const backup = String(fallback || '').trim();
    return backup || `field_${Math.random().toString(16).slice(2, 10)}`;
}

export function normalizeFormFieldOverride(value, fallbackKind = 'text') {
    const kind = fallbackKind === 'checkbox' ? 'checkbox' : 'text';

    if (value && typeof value === 'object') {
        if (value.kind === 'checkbox') {
            return {
                kind: 'checkbox',
                checked: !!value.checked,
            };
        }

        if (value.kind === 'text') {
            return {
                kind: 'text',
                value: String(value.value ?? ''),
            };
        }
    }

    if (kind === 'checkbox') {
        const raw = String(value ?? '').trim().toLowerCase();
        const checked = raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on';
        return {
            kind: 'checkbox',
            checked,
        };
    }

    return {
        kind: 'text',
        value: String(value ?? ''),
    };
}

export async function extractPageFormFields(pdfPage, viewport, pageNumber) {
    let annotations;
    try {
        annotations = await pdfPage.getAnnotations({ intent: 'display' });
    } catch (error) {
        return [];
    }

    if (!Array.isArray(annotations) || !annotations.length) {
        return [];
    }

    const fields = [];
    let fieldIndex = 0;

    annotations.forEach((item) => {
        if (!item || String(item.subtype || '').toLowerCase() !== 'widget') {
            return;
        }

        const fieldType = String(item.fieldType || '').toLowerCase();
        let kind = null;
        if (fieldType === 'tx') {
            kind = 'text';
        } else if (fieldType === 'btn' && !item.radioButton && !item.pushButton) {
            kind = 'checkbox';
        }

        if (!kind) {
            return;
        }

        const rect = Array.isArray(item.rect) ? item.rect : null;
        if (!rect || rect.length !== 4) {
            return;
        }

        // pdf.js 6 removed PageViewport.convertToViewportRectangle (Sep 2026,
        // pdfjs-dist 5.7 → 6.3): map the two corners ourselves.
        const [vx1, vy1] = viewport.convertToViewportPoint(rect[0], rect[1]);
        const [vx2, vy2] = viewport.convertToViewportPoint(rect[2], rect[3]);
        const viewportRect = [vx1, vy1, vx2, vy2];
        if (viewportRect.some((value) => !Number.isFinite(value))) {
            return;
        }

        const x = Math.min(viewportRect[0], viewportRect[2]);
        const y = Math.min(viewportRect[1], viewportRect[3]);
        const width = Math.abs(viewportRect[2] - viewportRect[0]);
        const height = Math.abs(viewportRect[3] - viewportRect[1]);

        // Size sanity in PDF points, never render pixels: a phone (or a narrow
        // pane) renders a page small enough that every 14pt field on a
        // government form fell under an 8px floor and the form "had no fields".
        const minSize = kind === 'checkbox' ? 4 : 8;
        const pdfWidth = Math.abs(Number(rect[2]) - Number(rect[0]));
        const pdfHeight = Math.abs(Number(rect[3]) - Number(rect[1]));
        if (pdfWidth < minSize || pdfHeight < minSize || width < 1 || height < 1) {
            return;
        }

        const fallbackName = `field_${pageNumber}_${fieldIndex + 1}`;
        const fieldName = normalizeFormFieldName(item.fieldName || item.fullName || item.id, fallbackName);
        const rawValue = item.fieldValue == null ? '' : String(item.fieldValue);
        const maxLengthRaw = Number(item.maxLen);
        const fieldFlags = Number(item.fieldFlags || 0);
        const readOnly = !!item.readOnly || !!(fieldFlags & 1);
        const baseField = {
            id: String(item.id || `${fieldName}_${fieldIndex + 1}`),
            page: pageNumber,
            name: fieldName,
            // The widget's tooltip (/TU), the human label on many government forms.
            label: String(item.alternativeText || '').trim(),
            kind,
            x,
            y,
            width,
            height,
            readOnly,
        };

        if (kind === 'checkbox') {
            const checked = rawValue !== '' && rawValue.toLowerCase() !== 'off';
            fields.push({
                ...baseField,
                value: rawValue,
                checked,
                exportValue: String(item.exportValue || 'Yes'),
            });
        } else {
            fields.push({
                ...baseField,
                value: rawValue,
                maxLength: Number.isFinite(maxLengthRaw) && maxLengthRaw > 0 ? maxLengthRaw : null,
            });
        }

        fieldIndex += 1;
    });

    return fields;
}

export function hasHeuristicFieldOverlap(existingFields, candidate) {
    const candidateArea = Math.max(1, Number(candidate.width || 0) * Number(candidate.height || 0));
    for (const existing of existingFields) {
        if (!existing || existing.kind !== candidate.kind || Number(existing.page || 0) !== Number(candidate.page || 0)) {
            continue;
        }

        const overlapArea = getRectIntersectionArea(existing, candidate);
        if (overlapArea <= 0) {
            continue;
        }

        const existingArea = Math.max(1, Number(existing.width || 0) * Number(existing.height || 0));
        const overlapRatio = overlapArea / Math.min(candidateArea, existingArea);
        if (overlapRatio >= 0.62) {
            return true;
        }
    }

    return false;
}

export function detectHeuristicFormFieldsFromTextFragments(textFragments, pageNumber, layout) {
    if (!Array.isArray(textFragments) || !textFragments.length || !layout) {
        return [];
    }

    const generated = [];
    let nextIndex = 1;
    const pageWidth = Math.max(1, Number(layout.screenWidth || 1));
    const pageHeight = Math.max(1, Number(layout.screenHeight || 1));

    const pushField = (field) => {
        if (!field) {
            return;
        }
        if (hasHeuristicFieldOverlap(generated, field)) {
            return;
        }
        generated.push(field);
        nextIndex += 1;
    };

    textFragments.forEach((fragment) => {
        const text = String(fragment && fragment.text ? fragment.text : '');
        if (!text) {
            return;
        }

        const fragmentX = Number(fragment.x || 0);
        const fragmentY = Number(fragment.y || 0);
        const fragmentWidth = Math.max(1, Number(fragment.width || 0));
        const fragmentHeight = Math.max(8, Number(fragment.height || 0));
        const characterWidth = Math.max(2.1, fragmentWidth / Math.max(1, text.length));

        // Detect checkbox-like glyphs (ex: ☐ ☑ □).
        for (let charIndex = 0; charIndex < text.length; charIndex += 1) {
            const char = text.charAt(charIndex);
            if (!/[☐☑☒□▢■✅✔✕✖✗✘]/u.test(char)) {
                continue;
            }

            const boxSize = clamp(Math.max(11, Math.min(fragmentHeight * 0.96, 22)), 10, 24);
            const boxX = clamp(
                fragmentX + (charIndex * characterWidth) - Math.max(0.6, characterWidth * 0.16),
                0,
                Math.max(1, pageWidth - boxSize)
            );
            const boxY = clamp(
                fragmentY + Math.max(0, (fragmentHeight - boxSize) / 2),
                0,
                Math.max(1, pageHeight - boxSize)
            );
            const checked = isLikelyCheckedGlyph(char);

            pushField({
                id: `heuristic_cb_${pageNumber}_${nextIndex}`,
                page: pageNumber,
                name: `heuristic_checkbox_${pageNumber}_${nextIndex}`,
                kind: 'checkbox',
                x: boxX,
                y: boxY,
                width: boxSize,
                height: boxSize,
                readOnly: false,
                checked,
                value: checked ? 'Yes' : 'Off',
                exportValue: 'Yes',
                heuristic: true,
            });
        }

        // Detect underline-like runs (ex: _________ or -----) and convert them to text fill targets.
        const underlineRunPattern = /[_\u2014\u2013\-]{3,}/g;
        let match;
        while ((match = underlineRunPattern.exec(text)) !== null) {
            const runText = String(match[0] || '');
            if (!isLikelyUnderlineRun(runText)) {
                continue;
            }

            const runStart = Number(match.index || 0);
            const runLength = runText.length;
            const runWidth = Math.max(24, runLength * characterWidth);

            const stripped = text.replace(/\s+/g, '');
            const strippedLineChars = (stripped.match(/[_\u2014\u2013\-]/g) || []).length;
            const lineOnlyFragment = stripped.length > 0 && (strippedLineChars / stripped.length) >= 0.92;
            if (lineOnlyFragment && runWidth > (pageWidth * 0.72)) {
                continue;
            }

            const fieldWidth = clamp(runWidth, 18, Math.max(20, pageWidth - 2));
            const lineYScreen = fragmentY + Math.max(1, fragmentHeight * 0.88);
            const fieldHeight = clamp(Math.max(16, fragmentHeight * 1.05), 14, 28);
            const fieldX = clamp(fragmentX + (runStart * characterWidth), 0, Math.max(1, pageWidth - fieldWidth));
            const fieldY = clamp(
                lineYScreen - (fieldHeight * 0.58),
                0,
                Math.max(1, pageHeight - fieldHeight)
            );

            pushField({
                id: `heuristic_txt_${pageNumber}_${nextIndex}`,
                page: pageNumber,
                name: `heuristic_text_${pageNumber}_${nextIndex}`,
                kind: 'text',
                x: fieldX,
                y: fieldY,
                width: fieldWidth,
                height: fieldHeight,
                readOnly: false,
                value: '',
                maxLength: null,
                heuristic: true,
                heuristicSource: 'underline-run',
                lineYScreen,
            });
        }
    });

    return generated;
}

export function detectHeuristicLineFieldsFromCanvas(pageNumber, layout, existingFields = [], textFragments = []) {
    if (!layout) {
        return [];
    }

    const sourceCanvas = getPageCanvasElement(pageNumber);
    if (!sourceCanvas) {
        return [];
    }

    const sampleWidth = Math.max(1, Math.round(Number(layout.screenWidth || 1)));
    const sampleHeight = Math.max(1, Math.round(Number(layout.screenHeight || 1)));
    if (sampleWidth < 120 || sampleHeight < 120) {
        return [];
    }

    const sampleCanvas = document.createElement('canvas');
    sampleCanvas.width = sampleWidth;
    sampleCanvas.height = sampleHeight;
    const sampleContext = sampleCanvas.getContext('2d', { willReadFrequently: true });
    if (!sampleContext) {
        return [];
    }

    sampleContext.drawImage(sourceCanvas, 0, 0, sampleWidth, sampleHeight);

    let imageData;
    try {
        imageData = sampleContext.getImageData(0, 0, sampleWidth, sampleHeight);
    } catch (error) {
        return [];
    }
    const pixelData = imageData.data;
    if (!pixelData || !pixelData.length) {
        return [];
    }

    const rowSegments = [];
    const minRunLength = Math.max(42, Math.floor(sampleWidth * 0.08));
    for (let y = 0; y < sampleHeight; y += 1) {
        let x = 0;
        while (x < sampleWidth) {
            const startX = x;
            while (x < sampleWidth) {
                const pixelIndex = ((y * sampleWidth) + x) * 4;
                if (isPixelLikelyLine(pixelData, pixelIndex)) {
                    break;
                }
                x += 1;
            }

            if (x >= sampleWidth) {
                break;
            }

            const runStart = x;
            while (x < sampleWidth) {
                const pixelIndex = ((y * sampleWidth) + x) * 4;
                if (!isPixelLikelyLine(pixelData, pixelIndex)) {
                    break;
                }
                x += 1;
            }
            const runEnd = x - 1;
            const runLength = (runEnd - runStart) + 1;
            if (runLength >= minRunLength) {
                rowSegments.push({
                    y,
                    x1: runStart,
                    x2: runEnd,
                });
            }

            if (x <= startX) {
                x += 1;
            }
        }
    }

    if (!rowSegments.length) {
        return [];
    }

    const groups = [];
    rowSegments.forEach((segment) => {
        let matchedGroup = null;
        for (let index = groups.length - 1; index >= 0; index -= 1) {
            const group = groups[index];
            if (segment.y > (group.maxY + 2)) {
                break;
            }
            const sameLineBand = Math.abs(segment.y - group.maxY) <= 2;
            const similarX = Math.abs(segment.x1 - group.avgX1) <= 14 && Math.abs(segment.x2 - group.avgX2) <= 14;
            if (sameLineBand && similarX) {
                matchedGroup = group;
                break;
            }
        }

        if (!matchedGroup) {
            groups.push({
                minY: segment.y,
                maxY: segment.y,
                avgX1: segment.x1,
                avgX2: segment.x2,
                count: 1,
            });
            return;
        }

        matchedGroup.maxY = Math.max(matchedGroup.maxY, segment.y);
        matchedGroup.avgX1 = ((matchedGroup.avgX1 * matchedGroup.count) + segment.x1) / (matchedGroup.count + 1);
        matchedGroup.avgX2 = ((matchedGroup.avgX2 * matchedGroup.count) + segment.x2) / (matchedGroup.count + 1);
        matchedGroup.count += 1;
    });

    const generated = [];
    let nextIndex = 1;
    const pageTextFragments = Array.isArray(textFragments) ? textFragments : [];

    groups.forEach((group) => {
        const y = Math.round((group.minY + group.maxY) / 2);
        const thickness = (group.maxY - group.minY) + 1;
        const x1 = Math.round(group.avgX1);
        const x2 = Math.round(group.avgX2);
        const length = (x2 - x1) + 1;

        if (length < minRunLength || thickness > 7) {
            return;
        }
        if (y < (sampleHeight * 0.07) || y > (sampleHeight * 0.965)) {
            return;
        }
        if (length > (sampleWidth * 0.9)) {
            return;
        }

        const lineRect = {
            x: x1,
            y: Math.max(0, y - 2),
            width: Math.max(1, length),
            height: Math.max(4, thickness + 4),
        };

        // Skip visual lines that overlap existing printed text content.
        const overlapsText = pageTextFragments.some((fragment) => {
            if (!fragment || !String(fragment.text || '').trim()) {
                return false;
            }
            const fragmentRect = {
                x: Number(fragment.x || 0),
                y: Number(fragment.y || 0),
                width: Math.max(1, Number(fragment.width || 0)),
                height: Math.max(1, Number(fragment.height || 0)),
            };
            const overlapArea = getRectIntersectionArea(lineRect, fragmentRect);
            if (overlapArea <= 0) {
                return false;
            }
            const minArea = Math.min(
                Math.max(1, lineRect.width * lineRect.height),
                Math.max(1, fragmentRect.width * fragmentRect.height)
            );
            return (overlapArea / minArea) >= 0.2;
        });
        if (overlapsText) {
            return;
        }

        let adjustedX = x1;
        const lineEndX = x2;
        const labelCandidate = pageTextFragments
            .filter((fragment) => {
                if (!fragment || !String(fragment.text || '').trim()) {
                    return false;
                }
                const fragmentText = String(fragment.text || '').trim();
                if (!fragmentText.endsWith(':')) {
                    return false;
                }
                const fragmentY = Number(fragment.y || 0);
                const fragmentHeight = Math.max(1, Number(fragment.height || 0));
                const verticalCenter = fragmentY + (fragmentHeight / 2);
                if (Math.abs(verticalCenter - y) > 15) {
                    return false;
                }
                const fragmentEndX = Number(fragment.x || 0) + Math.max(1, Number(fragment.width || 0));
                const gap = adjustedX - fragmentEndX;
                return gap >= -6 && gap <= 160;
            })
            .sort((left, right) => {
                const leftGap = adjustedX - (Number(left.x || 0) + Math.max(1, Number(left.width || 0)));
                const rightGap = adjustedX - (Number(right.x || 0) + Math.max(1, Number(right.width || 0)));
                return Math.abs(leftGap) - Math.abs(rightGap);
            })[0];

        if (labelCandidate) {
            const labelEndX = Number(labelCandidate.x || 0) + Math.max(1, Number(labelCandidate.width || 0));
            adjustedX = Math.max(adjustedX, Math.round(labelEndX + 4));
        }

        const adjustedWidth = Math.max(1, (lineEndX - adjustedX) + 1);
        if (adjustedWidth < Math.max(22, minRunLength * 0.35)) {
            return;
        }

        const fieldHeight = clamp(Math.max(19, thickness * 4.4), 18, 30);
            const field = {
                id: `heuristic_line_${pageNumber}_${nextIndex}`,
                page: pageNumber,
                name: `heuristic_line_text_${pageNumber}_${nextIndex}`,
                kind: 'text',
            x: adjustedX,
            y: clamp(y - (fieldHeight * 0.72), 0, Math.max(1, sampleHeight - fieldHeight)),
            width: adjustedWidth,
            height: fieldHeight,
                readOnly: false,
                value: '',
                maxLength: null,
                heuristic: true,
                heuristicSource: 'line',
                lineYScreen: y,
            };

        if (hasHeuristicFieldOverlap(existingFields, field) || hasHeuristicFieldOverlap(generated, field)) {
            return;
        }

        generated.push(field);
        nextIndex += 1;
    });

    return generated;
}

export function addFormFieldsToLoadedValuesMap(loadedFormValues) {
    for (const fields of state.pageFormFields.values()) {
        if (!Array.isArray(fields)) {
            continue;
        }

        fields.forEach((field) => {
            const key = normalizeFormFieldName(field.name, field.id);
            if (loadedFormValues.has(key)) {
                return;
            }

            loadedFormValues.set(key, field.kind === 'checkbox'
                ? {
                    kind: 'checkbox',
                    checked: !!field.checked,
                }
                : {
                    kind: 'text',
                    value: String(field.value != null ? field.value : ''),
                });
        });
    }
}

export function maybeGenerateHeuristicFormFieldFallback() {
    const pageNumbers = Array.from(state.pageLayouts.keys())
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value))
        .sort((a, b) => a - b);

    if (!pageNumbers.length) {
        return 0;
    }

    const nativeFieldCount = pageNumbers.reduce((count, pageNumber) => {
        const fields = getFormFieldsForPage(pageNumber);
        const nativeCount = fields.filter((field) => !field.heuristic).length;
        return count + nativeCount;
    }, 0);

    if (nativeFieldCount > 0) {
        return 0;
    }

    let generatedCount = 0;
    pageNumbers.forEach((pageNumber) => {
        const layout = state.pageLayouts.get(pageNumber);
        const textFragments = state.pageTextFragments.get(pageNumber) || [];
        const generatedFields = detectHeuristicFormFieldsFromTextFragments(textFragments, pageNumber, layout);
        const lineFields = detectHeuristicLineFieldsFromCanvas(pageNumber, layout, generatedFields, textFragments);
        const mergedFields = generatedFields.concat(lineFields).sort((left, right) => {
            const yDiff = Number(left.y || 0) - Number(right.y || 0);
            if (Math.abs(yDiff) > 1.2) {
                return yDiff;
            }
            return Number(left.x || 0) - Number(right.x || 0);
        });
        state.pageFormFields.set(pageNumber, mergedFields);
        generatedCount += mergedFields.length;
    });

    return generatedCount;
}

export function getFormFieldsForPage(pageNumber) {
    const fields = state.pageFormFields.get(pageNumber);
    return Array.isArray(fields) ? fields : [];
}

export function getFormFieldChecked(field) {
    const name = normalizeFormFieldName(field && field.name, field && field.id);
    if (state.formFieldOverrides.has(name)) {
        const override = normalizeFormFieldOverride(state.formFieldOverrides.get(name), 'checkbox');
        return !!override.checked;
    }

    return !!(field && field.checked);
}

export function getFormFieldDisplayValue(field) {
    const name = normalizeFormFieldName(field && field.name, field && field.id);
    if (state.formFieldOverrides.has(name)) {
        const override = normalizeFormFieldOverride(state.formFieldOverrides.get(name), field && field.kind);
        if (override.kind === 'text') {
            return String(override.value ?? '');
        }
    }

    return String(field && field.value != null ? field.value : '');
}

export function getTextFormFieldOverrideValue(field) {
    if (!field || field.kind !== 'text') {
        return null;
    }

    const name = normalizeFormFieldName(field.name, field.id);
    if (!state.formFieldOverrides.has(name)) {
        return null;
    }

    const override = normalizeFormFieldOverride(state.formFieldOverrides.get(name), 'text');
    if (override.kind !== 'text') {
        return null;
    }

    return String(override.value ?? '');
}

export function isFormFieldOverrideEqualToLoaded(overrideValue, loadedValue) {
    if (!loadedValue || typeof loadedValue !== 'object') {
        return false;
    }

    const override = normalizeFormFieldOverride(overrideValue, loadedValue.kind || 'text');
    if ((loadedValue.kind || 'text') === 'checkbox') {
        return !!override.checked === !!loadedValue.checked;
    }

    return String(override.value ?? '') === String(loadedValue.value ?? '');
}

export function findFormFieldAtPoint(pageNumber, x, y) {
    const fields = getFormFieldsForPage(pageNumber);
    for (let index = fields.length - 1; index >= 0; index -= 1) {
        const field = fields[index];
        const hitPadding = field.kind === 'checkbox' ? 6 : 0;
        const withinX = x >= (field.x - hitPadding) && x <= (field.x + field.width + hitPadding);
        const withinY = y >= (field.y - hitPadding) && y <= (field.y + field.height + hitPadding);
        if (withinX && withinY) {
            return field;
        }
    }

    return null;
}

export function getFormFieldOverlaySvg(pageNumber) {
    if (state.activeSuiteTool !== 'edit') {
        return '';
    }
    const isFormFillMode = state.activeTool === 'form-fill';

    const fields = getFormFieldsForPage(pageNumber);
    if (!fields.length) {
        return '';
    }
    const pageTextAnnotations = state.annotations.filter((annotation) => annotation.type === 'text' && annotation.page === pageNumber);
    const pageTextFragments = Array.isArray(state.pageTextFragments.get(pageNumber))
        ? state.pageTextFragments.get(pageNumber)
        : [];

    const activeFieldName = isFormFillMode && state.activeFormFieldEditor
        ? normalizeFormFieldName(state.activeFormFieldEditor.fieldName)
        : '';

    return fields.map((field) => {
        const fieldName = normalizeFormFieldName(field.name, field.id);
        const hasOverride = state.formFieldOverrides.has(fieldName);
        if (!isFormFillMode && !hasOverride) {
            return '';
        }

        const isActive = isFormFillMode && activeFieldName === fieldName;
        const fill = field.readOnly
            ? 'rgba(148,163,184,0.17)'
            : (isActive
                ? 'rgba(191,219,254,0.45)'
                : (isFormFillMode ? 'rgba(219,234,254,0.32)' : 'rgba(191,219,254,0.2)'));
        const stroke = field.readOnly
            ? '#94a3b8'
            : (isActive ? '#1d4ed8' : (isFormFillMode ? '#3b82f6' : '#60a5fa'));

        if (field.kind === 'checkbox') {
            const checked = getFormFieldChecked(field);
            const boxSize = Math.max(10, Math.min(field.width, field.height));
            const boxX = field.x + ((field.width - boxSize) / 2);
            const boxY = field.y + ((field.height - boxSize) / 2);
            const checkStroke = field.readOnly ? '#64748b' : '#1d4ed8';
            const checkPath = checked
                ? `<path d="M ${boxX + (boxSize * 0.18)} ${boxY + (boxSize * 0.56)} L ${boxX + (boxSize * 0.41)} ${boxY + (boxSize * 0.78)} L ${boxX + (boxSize * 0.84)} ${boxY + (boxSize * 0.24)}" fill="none" stroke="${checkStroke}" stroke-width="${Math.max(1.3, boxSize * 0.16)}" stroke-linecap="round" stroke-linejoin="round"></path>`
                : '';
            const helperLabel = isFormFillMode
                ? (checked ? 'Checked' : (field.readOnly ? 'Read only' : ''))
                : (checked ? 'Checked' : 'Unchecked');
            const labelY = boxY + boxSize + 11;
            const dashPattern = field.readOnly || !isFormFillMode ? 'none' : '4 3';
            const overlayOpacity = isFormFillMode && activeFieldName && activeFieldName !== fieldName ? 0.22 : 1;

            return `
                <g opacity="${overlayOpacity}">
                    <rect x="${field.x}" y="${field.y}" width="${field.width}" height="${field.height}" rx="3" ry="3" fill="${fill}" stroke="${stroke}" stroke-width="${isActive ? 2.1 : 1.15}" stroke-dasharray="${dashPattern}"></rect>
                    <rect x="${boxX}" y="${boxY}" width="${boxSize}" height="${boxSize}" rx="1.5" ry="1.5" fill="${checked ? 'rgba(59,130,246,0.18)' : '#ffffff'}" stroke="${stroke}" stroke-width="${isActive ? 1.85 : 1.2}"></rect>
                    ${checkPath}
                    ${helperLabel
                        ? `<text x="${boxX + boxSize + 5}" y="${labelY}" fill="${checked ? '#0f172a' : '#1d4ed8'}" font-size="9.5" font-family="Helvetica, Arial, sans-serif" font-weight="${checked ? '600' : '700'}">${escapeSvg(helperLabel)}</text>`
                        : ''}
                </g>
            `;
        }

        const displayValue = getFormFieldDisplayValue(field).trim();
        const overrideValue = getTextFormFieldOverrideValue(field);
        const hasOverrideValue = overrideValue !== null;
        const hasVisibleText = displayValue === ''
            ? fieldHasVisibleTextContent(pageNumber, field, pageTextAnnotations, pageTextFragments)
            : false;
        const isFieldOccupied = displayValue !== '' || hasVisibleText;
        const helperLabel = field.readOnly ? 'Read only' : '';
        const showHelperText = isFormFillMode && !isFieldOccupied && helperLabel !== '';
        const shortLabel = helperLabel.length > 82 ? `${helperLabel.slice(0, 82)}...` : helperLabel;
        const textColor = field.readOnly ? '#475569' : '#1d4ed8';
        const fontSize = Math.max(9.5, Math.min(15, field.height * 0.52));
        const textY = field.y + Math.min(field.height - 3, Math.max(fontSize + 2, (field.height + fontSize) / 2));
        const previewRaw = String(overrideValue ?? '').replace(/\r/g, '').replace(/\n+/g, ' ').trim();
        const previewMaxChars = Math.max(
            4,
            Math.floor((Math.max(10, field.width - 14)) / Math.max(5.4, fontSize * 0.56))
        );
        const previewLabel = previewRaw.length > previewMaxChars
            ? `${previewRaw.slice(0, Math.max(1, previewMaxChars - 3))}...`
            : previewRaw;
        const showStagedEmpty = !isFormFillMode && hasOverride && previewRaw === '';
        const fieldFill = field.readOnly
            ? fill
            : (isActive
                ? 'rgba(147,197,253,0.62)'
                : (isFieldOccupied ? 'rgba(191,219,254,0.18)' : fill));
        const dashPattern = (field.readOnly || isFieldOccupied || !isFormFillMode) ? 'none' : '4 3';
        const overlayOpacity = isFormFillMode && activeFieldName && activeFieldName !== fieldName ? 0.22 : 1;

        return `
            <g opacity="${overlayOpacity}">
                <rect x="${field.x}" y="${field.y}" width="${field.width}" height="${field.height}" rx="3" ry="3" fill="${fieldFill}" stroke="${stroke}" stroke-width="${isActive ? 2.1 : 1.15}" stroke-dasharray="${dashPattern}"></rect>
                ${hasOverrideValue
                    ? `<rect x="${field.x + 1}" y="${field.y + 1}" width="${Math.max(0, field.width - 2)}" height="${Math.max(0, field.height - 2)}" rx="2" ry="2" fill="rgba(255,255,255,0.93)" stroke="none"></rect>`
                    : ''}
                ${showHelperText
                    ? `<text x="${field.x + 6}" y="${textY}" fill="${textColor}" font-size="${fontSize}" font-family="Helvetica, Arial, sans-serif" font-weight="700">${escapeSvg(shortLabel)}</text>`
                    : (hasOverrideValue && previewLabel
                        ? `<text x="${field.x + 6}" y="${textY}" fill="#1d4ed8" font-size="${fontSize}" font-family="Helvetica, Arial, sans-serif" font-weight="600">${escapeSvg(previewLabel)}</text>`
                        : (showStagedEmpty
                            ? `<text x="${field.x + 6}" y="${textY}" fill="#64748b" font-size="${Math.max(9, fontSize - 0.5)}" font-family="Helvetica, Arial, sans-serif" font-weight="600">Cleared</text>`
                            : ''))}
            </g>
        `;
    }).join('');
}

export function getOrderedEditableTextFormFields() {
    const pageNumbers = Array.from(state.pageFormFields.keys())
        .map((pageNumber) => Number(pageNumber))
        .filter((pageNumber) => Number.isFinite(pageNumber))
        .sort((a, b) => a - b);
    const orderedFields = [];

    pageNumbers.forEach((pageNumber) => {
        const fields = getFormFieldsForPage(pageNumber)
            .filter((field) => field && field.kind === 'text' && !field.readOnly)
            .sort((left, right) => {
                const yDiff = Number(left.y || 0) - Number(right.y || 0);
                if (Math.abs(yDiff) > 1.5) {
                    return yDiff;
                }

                const xDiff = Number(left.x || 0) - Number(right.x || 0);
                if (Math.abs(xDiff) > 1.5) {
                    return xDiff;
                }

                return String(left.id || '').localeCompare(String(right.id || ''));
            });

        fields.forEach((field) => {
            orderedFields.push({
                page: pageNumber,
                field,
            });
        });
    });

    return orderedFields;
}

export function focusAdjacentFormField(fromPageNumber, fromFieldId, direction = 1) {
    const orderedFields = getOrderedEditableTextFormFields();
    if (!orderedFields.length) {
        setStatus('No editable text fields detected');
        return false;
    }

    const step = direction < 0 ? -1 : 1;
    const currentIndex = orderedFields.findIndex((entry) => (
        Number(entry.page) === Number(fromPageNumber) &&
        String(entry.field && entry.field.id ? entry.field.id : '') === String(fromFieldId || '')
    ));
    const targetIndex = currentIndex >= 0
        ? currentIndex + step
        : (step > 0 ? 0 : orderedFields.length - 1);

    if (targetIndex < 0 || targetIndex >= orderedFields.length) {
        setStatus(step > 0 ? 'Reached last fillable field' : 'Reached first fillable field');
        return false;
    }

    const target = orderedFields[targetIndex];
    const targetPageShell = pagesEl.querySelector(`.pdf-page-shell[data-page="${target.page}"]`);
    if (!targetPageShell) {
        return false;
    }

    state.lastActivePage = target.page;
    targetPageShell.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.requestAnimationFrame(() => {
        if (state.activeSuiteTool !== 'edit' || state.activeTool !== 'form-fill') {
            return;
        }
        openInlineFormFieldEditor(targetPageShell, target.page, target.field);
    });

    return true;
}

export function jumpToFormFillField(direction = 1) {
    const step = direction < 0 ? -1 : 1;
    if (state.activeSuiteTool !== 'edit' || state.activeTool !== 'form-fill') {
        setStatus('Open Form Fill mode to navigate fields');
        return false;
    }

    const activeEditor = state.activeFormFieldEditor;
    if (activeEditor) {
        const fromPageNumber = Number(activeEditor.page || state.lastActivePage || 1);
        const fromFieldId = String(activeEditor.fieldId || '');
        commitInlineFormFieldEditor(true);
        return focusAdjacentFormField(fromPageNumber, fromFieldId, step);
    }

    const orderedFields = getOrderedEditableTextFormFields();
    if (!orderedFields.length) {
        setStatus('No editable text fields detected');
        return false;
    }

    const currentPage = Number(state.lastActivePage || 1);
    let target = null;
    if (step > 0) {
        target = orderedFields.find((entry) => Number(entry.page) >= currentPage) || orderedFields[0];
    } else {
        const reversed = orderedFields.slice().reverse();
        target = reversed.find((entry) => Number(entry.page) <= currentPage) || reversed[0];
    }

    if (!target) {
        return false;
    }

    const targetPageShell = pagesEl.querySelector(`.pdf-page-shell[data-page="${target.page}"]`);
    if (!targetPageShell) {
        return false;
    }

    state.lastActivePage = Number(target.page);
    targetPageShell.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.requestAnimationFrame(() => {
        if (state.activeSuiteTool !== 'edit' || state.activeTool !== 'form-fill') {
            return;
        }
        openInlineFormFieldEditor(targetPageShell, target.page, target.field);
    });
    return true;
}

export function commitInlineFormFieldEditor(saveValue = true) {
    if (!state.activeFormFieldEditor || !state.activeFormFieldEditor.el) {
        return;
    }

    const activeEditor = state.activeFormFieldEditor;
    const nextValue = String(activeEditor.input.value || '').replace(/\r/g, '');
    const fieldName = normalizeFormFieldName(activeEditor.fieldName);
    const originalValue = String(activeEditor.originalValue != null ? activeEditor.originalValue : '');

    activeEditor.el.remove();
    state.activeFormFieldEditor = null;

    if (saveValue) {
        const hadOverride = state.formFieldOverrides.has(fieldName);
        if (nextValue !== originalValue || hadOverride) {
            pushHistory('form field');
        }
        if (nextValue === originalValue) {
            state.formFieldOverrides.delete(fieldName);
            setStatus('Form field unchanged');
        } else {
            state.formFieldOverrides.set(fieldName, {
                kind: 'text',
                value: nextValue,
            });
            setStatus('Form field updated');
        }
        scheduleDraftSave();
    }

    redrawPage(activeEditor.page);
}

export function toggleCheckboxFormField(field, pageNumber) {
    const fieldName = normalizeFormFieldName(field.name, field.id);
    const currentChecked = getFormFieldChecked(field);
    const nextChecked = !currentChecked;
    pushHistory('checkbox');

    if (nextChecked === !!field.checked) {
        state.formFieldOverrides.delete(fieldName);
    } else {
        state.formFieldOverrides.set(fieldName, {
            kind: 'checkbox',
            checked: nextChecked,
        });
    }

    redrawPage(pageNumber);
    scheduleDraftSave();
    setStatus(nextChecked ? `Checked ${fieldName}` : `Unchecked ${fieldName}`);
}

export function openInlineFormFieldEditor(pageShell, pageNumber, field) {
    commitInlineFormFieldEditor(true);

    const fieldName = normalizeFormFieldName(field.name, field.id);
    const currentValue = getFormFieldDisplayValue(field);
    const editorWidth = Math.max(88, field.width);
    const editorHeight = Math.max(28, field.height);
    const x = clamp(field.x, 0, Math.max(1, (state.pageLayouts.get(pageNumber)?.screenWidth || editorWidth) - editorWidth));
    const y = clamp(field.y, 0, Math.max(1, (state.pageLayouts.get(pageNumber)?.screenHeight || editorHeight) - editorHeight));

    const wrapper = document.createElement('div');
    wrapper.className = 'pdf-inline-form-field-editor';
    wrapper.style.left = `${x}px`;
    wrapper.style.top = `${y}px`;
    wrapper.style.width = `${editorWidth}px`;
    wrapper.style.height = `${editorHeight}px`;

    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = field.readOnly ? 'Read only' : '';
    input.value = currentValue;
    input.spellcheck = false;
    if (field.maxLength && Number.isFinite(Number(field.maxLength))) {
        input.maxLength = Math.max(1, Number(field.maxLength));
    }

    wrapper.appendChild(input);
    pageShell.appendChild(wrapper);

    state.activeFormFieldEditor = {
        el: wrapper,
        input,
        page: pageNumber,
        fieldId: String(field.id || ''),
        fieldName,
        originalValue: String(field.value != null ? field.value : ''),
        committed: false,
    };

    wrapper.addEventListener('pointerdown', (event) => {
        event.stopPropagation();
    });

    input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            if (state.activeFormFieldEditor) {
                state.activeFormFieldEditor.committed = true;
            }
            commitInlineFormFieldEditor(false);
            return;
        }

        if (event.key === 'Enter' || event.key === 'Tab') {
            event.preventDefault();
            const activeEditor = state.activeFormFieldEditor;
            if (!activeEditor) {
                return;
            }

            activeEditor.committed = true;
            const fromPageNumber = Number(activeEditor.page || pageNumber);
            const fromFieldId = String(activeEditor.fieldId || field.id || '');
            const moveDirection = event.key === 'Tab' && event.shiftKey ? -1 : 1;
            commitInlineFormFieldEditor(true);
            focusAdjacentFormField(fromPageNumber, fromFieldId, moveDirection);
        }
    });

    input.addEventListener('blur', () => {
        const editorElement = wrapper;
        window.setTimeout(() => {
            if (!state.activeFormFieldEditor || state.activeFormFieldEditor.el !== editorElement) {
                return;
            }

            if (state.activeFormFieldEditor.committed) {
                state.activeFormFieldEditor = null;
                return;
            }

            commitInlineFormFieldEditor(true);
        }, 20);
    });

    input.focus();
    input.setSelectionRange(0, input.value.length);
    if (window.matchMedia('(max-width: 768px)').matches) {
        window.setTimeout(() => {
            if (!state.activeFormFieldEditor || state.activeFormFieldEditor.el !== wrapper) {
                return;
            }
            wrapper.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        }, 60);
    }
    setStatus(`Filling ${fieldName}...`);
    redrawPage(pageNumber);
}
